import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import {
  useCreditCardBills,
  useRecordCreditCardBillPayment,
  useUpsertCreditCardBill,
} from "@/hooks/api";
import { addMonths, formatDisplayDate, formatDisplayDateTime } from "@/lib/date.utils";
import { cn } from "@/lib/utils";
import { CircleAlert, CircleCheck } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CreditCardBillMonthEntry, CreditCardStatus } from "@/types";

interface BillsViewProps {
  month: string;
  status?: CreditCardStatus;
}

function ApproxBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-xs font-medium text-warning">
      <CircleAlert className="size-3" />
      approx
    </span>
  );
}

function GeneratedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-success/30 bg-success/10 px-1.5 py-0.5 text-xs font-medium text-success">
      <CircleCheck className="size-3" />
      generated
    </span>
  );
}

export function BillsView({ month, status = "active" }: BillsViewProps) {
  const navigate = useNavigate();
  // Show a 6-month window ending at the selected month.
  const startMonth = addMonths(month, -5);
  const { data: matrix, isLoading } = useCreditCardBills(startMonth, month, status);
  const recordPayment = useRecordCreditCardBillPayment();
  const upsertBill = useUpsertCreditCardBill();

  const [payingBill, setPayingBill] = useState<CreditCardBillMonthEntry | null>(null);
  const [payAmount, setPayAmount] = useState("");

  const monthsDesc = [...(matrix?.months ?? [])].reverse();

  if (isLoading && !matrix) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (!matrix || matrix.cards.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        No credit card accounts found.
      </div>
    );
  }

  const handlePay = async () => {
    if (!payingBill?.billId) return;
    const amount = Number(payAmount);
    if (!amount || amount <= 0) return;
    try {
      await recordPayment.mutateAsync({
        billId: payingBill.billId,
        data: { paidAmount: amount },
      });
    } finally {
      setPayingBill(null);
      setPayAmount("");
    }
  };

  return (
    <div className="space-y-6">
      {monthsDesc.map((m) => {
        const entries = matrix.cards.map((card) => ({
          card,
          entry: card.months.find((e) => e.month === m)!,
        }));
        const monthTotal = entries.reduce(
          (sum, { entry }) => sum + (entry.isGenerated ? (entry.amount ?? 0) : entry.approxAmount),
          0,
        );
        const monthLabel = new Date(
          Number(m.slice(0, 4)),
          Number(m.slice(5, 7)) - 1,
          1,
        ).toLocaleDateString("en-US", { month: "long", year: "numeric" });

        const generatedCount = entries.filter(({ entry }) => entry.isGenerated).length;
        const approxCount = entries.length - generatedCount;

        return (
          <section key={m} className="overflow-hidden rounded-lg border border-border">
            <div className="flex items-center justify-between border-b border-border bg-muted px-4 py-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium">{monthLabel}</h2>
                <span className="text-xs text-muted-foreground">
                  {generatedCount > 0 && `${generatedCount} generated`}
                  {generatedCount > 0 && approxCount > 0 && " · "}
                  {approxCount > 0 && `${approxCount} approx`}
                </span>
              </div>
              <span className="text-sm font-medium tabular-nums">
                <AmountDisplay amount={monthTotal} />
              </span>
            </div>
            <div className="divide-y divide-border">
              {entries.map(({ card, entry }) => {
                const amount = entry.isGenerated ? (entry.amount ?? 0) : entry.approxAmount;
                const fullyPaid = entry.paidAmount >= amount && amount > 0;
                return (
                  <div
                    key={`${card.accountId}-${m}`}
                    className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 -mx-1 text-left hover:bg-accent/40"
                      title={`View ${card.accountName} transactions for this cycle`}
                      onClick={() => {
                        navigate(
                          `/credit-cards?tab=transactions&accountId=${card.accountId}&startDate=${entry.cycleStart.slice(0, 10)}&endDate=${entry.cycleEnd.slice(0, 10)}`,
                        );
                      }}
                    >
                      {card.iconUrl ? (
                        <img src={card.iconUrl} alt="" className="size-5 shrink-0 rounded" />
                      ) : null}
                      <div className="min-w-0">
                        <p className="group/card-link truncate text-sm hover:underline">
                          {card.accountName}
                          {card.isArchived && (
                            <span className="ml-1 text-xs text-muted-foreground/70">
                              (Archived)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {entry.cycleStart.slice(0, 10)} → {entry.cycleEnd.slice(0, 10)}
                        </p>
                      </div>
                    </button>

                    <div className="flex items-center gap-3 sm:gap-4">
                      {entry.isGenerated ? <GeneratedBadge /> : <ApproxBadge />}
                      <div className="min-w-[110px] text-right">
                        <p
                          className={cn(
                            "text-sm font-medium tabular-nums",
                            !entry.isGenerated && "text-warning",
                          )}
                        >
                          <AmountDisplay amount={amount} />
                        </p>
                        {!entry.isGenerated && entry.approxAsOf && (
                          <p className="text-xs text-muted-foreground/70">
                            as of {formatDisplayDateTime(entry.approxAsOf)}
                          </p>
                        )}
                        {entry.dueDate && (
                          <p className="text-xs text-muted-foreground">
                            due {formatDisplayDate(entry.dueDate)}
                          </p>
                        )}
                      </div>
                      <div className="w-20 text-right">
                        {entry.paidAmount > 0 ? (
                          <p
                            className={cn(
                              "text-xs tabular-nums",
                              fullyPaid ? "text-success" : "text-muted-foreground",
                            )}
                          >
                            {fullyPaid ? "paid" : <AmountDisplay amount={entry.paidAmount} />}
                          </p>
                        ) : null}
                      </div>
                      {entry.billId && !fullyPaid && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={recordPayment.isPending}
                          onClick={() => {
                            setPayingBill(entry);
                            setPayAmount(String(amount || ""));
                          }}
                        >
                          Pay
                        </Button>
                      )}
                      {!entry.isGenerated && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={upsertBill.isPending}
                          title="Lock the approx amount in as a manual bill"
                          onClick={() => {
                            const card = matrix.cards.find((c) =>
                              c.months.some((m) => m === entry),
                            );
                            if (!card) return;
                            upsertBill.mutate({
                              accountId: card.accountId,
                              month: entry.month,
                              data: { isGenerated: true, amount: entry.approxAmount },
                            });
                          }}
                        >
                          Mark generated
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Pay dialog — mirrors the RecordPaymentDialog pattern */}
      <Dialog open={!!payingBill} onOpenChange={(open) => !open && setPayingBill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
            <DialogDescription>
              Bill for {payingBill?.month} ·{" "}
              {payingBill && (
                <AmountDisplay
                  amount={
                    payingBill.isGenerated ? (payingBill.amount ?? 0) : payingBill.approxAmount
                  }
                />
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="cc-pay-amount">Amount</Label>
            <Input
              id="cc-pay-amount"
              type="number"
              min="0"
              step="0.01"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setPayingBill(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handlePay} disabled={recordPayment.isPending}>
              Save payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-xs text-muted-foreground">
        <Badge variant="outline" className="mr-1 border-warning/30 text-warning">
          approx
        </Badge>
        amounts are live estimates from current spend — they become final when a statement is
        imported or the bill is marked generated.
      </p>
    </div>
  );
}
